import { Box, HStack } from "@chakra-ui/react";
import { FC, useCallback, useMemo, useState } from "react";
import { graphql, usePaginationFragment } from "react-relay";
import { NavLink } from "react-router-dom";

import { ScheduleOrder } from "~/src/__generated__/schema";
import { ScheduleListTab_user$key } from "~/src/__relay_artifacts__/ScheduleListTab_user.graphql";
import { SolidButton } from "~/src/components/common/Button";
import {
  type SortParams,
  Table,
  TableHeaderColumn,
  TableHeaderRow,
} from "~/src/components/common/tables/Table";
import {
  TablePagination,
  useTablePaginationProps,
} from "~/src/components/common/tables/TablePagination";
import { useUpdateDebounce } from "~/src/lib/react-use";

import {
  ScheduleTableDataRow,
  Props as ScheduleTableDataRowProps,
} from "./ScheduleTableDataRow";

export type Props = {
  userRef: ScheduleListTab_user$key;
  siteSlug: string;
  onDeleteClick: ScheduleTableDataRowProps["onDeleteClick"];
};

const userFragment = graphql`
  fragment ScheduleListTab_user on User
  @refetchable(queryName: "ScheduleListTab_Query")
  @argumentDefinitions(
    count: { type: "Int", defaultValue: 120 }
    cursor: { type: "String" }
    orderBy: {
      type: "ScheduleOrder"
      defaultValue: { field: START_TIME, direction: ASC }
    }
  ) {
    schedules(first: $count, after: $cursor, orderBy: $orderBy)
      @connection(key: "ScheduleListTab_schedules") {
      __id
      totalCount
      edges {
        node {
          id
          ...ScheduleTableDataRow_schedule
        }
      }
    }
  }
`;

export const ScheduleListTab: FC<Props> = ({
  userRef,
  siteSlug,
  onDeleteClick,
}) => {
  const [sort, setSort] = useState<ScheduleOrder>({
    field: "START_TIME",
    direction: "ASC",
  });

  const {
    data: siteOwner,
    loadNext,
    hasNext,
    refetch,
  } = usePaginationFragment(userFragment, userRef);
  const { tablePaginationProps } = useTablePaginationProps({
    totalCount: siteOwner.schedules.totalCount,
    hasNext,
    loadNext,
    refetch,
  });

  const rows = useMemo(() => {
    const edges = siteOwner.schedules.edges || [];
    const from = tablePaginationProps.from;
    const to = tablePaginationProps.to;
    return edges.slice(from, to).map((edge) => {
      const node = edge?.node;
      if (!node) throw new Error("assertion error");
      return node;
    });
  }, [
    siteOwner.schedules.edges,
    tablePaginationProps.from,
    tablePaginationProps.to,
  ]);

  const handleSortClick = useCallback(<F, D>(params: SortParams<F, D>) => {
    setSort({
      field: params.field as ScheduleOrder["field"],
      direction: params.direction as ScheduleOrder["direction"],
    });
  }, []);

  useUpdateDebounce(
    () => {
      refetch({
        orderBy: { field: sort.field, direction: sort.direction },
      });
    },
    500,
    [refetch, sort]
  );

  return (
    <Box my="16px">
      <HStack justifyContent="flex-end" mb="16px" spacing="16px">
        <NavLink to={`/sites/${siteSlug}/schedules/new`}>
          <SolidButton>登録</SolidButton>
        </NavLink>
      </HStack>
      <Table>
        <TableHeaderRow>
          <TableHeaderColumn field="START_TIME" onSortClick={handleSortClick}>
            開始
          </TableHeaderColumn>
          <TableHeaderColumn field="END_TIME" onSortClick={handleSortClick}>
            終了
          </TableHeaderColumn>
          <TableHeaderColumn>作成日時</TableHeaderColumn>
          <TableHeaderColumn>更新日時</TableHeaderColumn>
          <TableHeaderColumn>操作</TableHeaderColumn>
        </TableHeaderRow>
        {rows.map((row) => (
          <ScheduleTableDataRow
            key={row.id}
            scheduleRef={row}
            siteSlug={siteSlug}
            connectionId={siteOwner.schedules.__id}
            onDeleteClick={onDeleteClick}
          />
        ))}
      </Table>
      <TablePagination {...tablePaginationProps} />
    </Box>
  );
};
